import z, {ZodSchema, type ZodRawShape, type ZodObject} from 'zod'
import {Err, OK, okOrThrow} from 'shared/promise'
import {Uri} from 'shared/uri'
import {isBoolean} from 'remeda'

type ApiMd<T extends ZodSchema, R extends ZodSchema> = {
  /** the request schema */
  zReq: T
  /** the response schema for successful requests (not need to include ok) */
  zResOk?: R
  /** the response schema for error requests (not need to include ok) */
  zResErr?: R
}

export type ApiMds = {
  [K: string]: ApiMd<ZodSchema, ZodSchema>
}

type ClientProps<E extends ApiMds> = {
  md: E
  url: Uri
}

export const okSchema = <TOK extends ZodObject<any>>(schema: TOK) => z.object({ok: z.literal(true), ...schema.shape})

export type ApiRes<E extends ApiMds, K extends keyof E> =
  | OK<NonNullable<E[K]['zResOk']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResOk']>> : {}>
  | Err<NonNullable<E[K]['zResErr']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResErr']>> : {message: string}>

export const apiClient = <E extends ApiMds>(props: ClientProps<E>) => {
  const {md, url} = props

  const makePost =
    <K extends keyof E & string>(type: K, init?: RequestInit) =>
    (body: z.infer<E[K]['zReq']>, overrideInit?: RequestInit) => {
      const {zResOk, zResErr} = md[type]!
      return url
        .mergeQuery({type})
        .postJson(body, {...(overrideInit ?? init), schema: zResOk, errorSchema: zResErr}) as Promise<ApiRes<E, K>>
    }

  const post = <K extends keyof E & string>(type: K, body: z.infer<E[K]['zReq']>, init?: RequestInit) =>
    makePost(type, init)(body)

  /** Creates mutation props for useMutation hook that handles API endpoint posting
   * @param type - The endpoint key to use for the mutation
   * @returns Object containing mutationFn that posts to the endpoint and handles errors
   * @example
   * const mutation = useMutation(apiClient.muteProps('example'))
   */
  const muteProps = <K extends keyof E & string>(type: K) => ({
    mutationFn: (
      body: z.infer<E[K]['zReq']>
    ): Promise<OK<NonNullable<E[K]['zResOk']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResOk']>> : {}>> =>
      post(type, body).then(r => okOrThrow(r as any))
  })

  const queryProps = <K extends keyof E & string, const TKey extends Partial<z.infer<E[K]['zReq']>>>(
    type: K,
    key: TKey,
    options?: {
      onError?: (
        error: NonNullable<E[K]['zResErr']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResErr']>>
        : {message: string}
      ) => void
    }
  ) => {
    type TReq = z.infer<E[K]['zReq']>
    type TBody = Omit<TReq, keyof TKey>
    type TRes = NonNullable<E[K]['zResOk']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResOk']>> : {}
    type TErr = Err<
      NonNullable<E[K]['zResErr']> extends ZodSchema ? z.infer<NonNullable<E[K]['zResErr']>> : {message: string}
    >
    return {
      queryFn: (body: TBody): Promise<TRes> =>
        post(type, {...key, ...body} as TReq).then((r: any) => {
          if (options?.onError) if (r.ok === false) options.onError(r as TErr)
          return okOrThrow(r as any)
        }),
      queryKey: [type, key]
    }
  }

  return {post, md, muteProps, makePost, queryProps}
}

export type ApiClientType<T extends ApiMds> = ReturnType<typeof apiClient<T>>

export const zDefaultError = z.object({ok: z.literal(false), message: z.string()})
